import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, LOCALE_ID, NgModule, OnInit } from '@angular/core';
import { CUSTOMERENVIRONMENT, WINDOW } from '@app/common/app-tokens';
import { BreakPoints } from '@app/common/breakpoints';
import { Constants } from '@app/common/constants';
import { Utilities } from '@app/common/utilities';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { Change, ChangeType } from '@app/models/change';
import { LinkModule } from '@app/directives/link.directive';
import { SafeModule } from '@app/pipes/safe.pipe';
import { MyCollaborationActivitiesService } from '@app/services/my-collaboration-activities.service';
import { UrlHelperService } from '@app/services/url-helper.service';
import { UserService } from '@app/services/user.service';
import { Environment } from '@environments/interface.environment';
import { merge, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-my-collaboration-activities-widget',
  templateUrl: './my-collaboration-activities-widget.component.html',
  styleUrls: ['./my-collaboration-activities-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyCollaborationActivitiesWidgetComponent implements OnInit {

  myFrequentSites$: Observable<Map<string, Change[]>>;
  allSiteActivitiesUrl: string;
  rootUrl = this.urlHelperService.getAuthorityUrlWithoutQueryStrings(this.environment.coreWorkplaceSiteUrl);

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              @Inject(WINDOW) private window: Window,
              private myCollabActivityService: MyCollaborationActivitiesService,
              private userService: UserService,
              private urlHelperService: UrlHelperService,
              @Inject(LOCALE_ID) public localeId: string) {
  }

  ngOnInit() {
    this.allSiteActivitiesUrl = this.rootUrl + '/_layouts/15/sharepoint.aspx';

    // Vary amount of items to show based on screen size (and thus breakpoint).
    // Goal: 1) display even amount, and 2) show suitable amount for the resolution
    const amountToLoad = { [BreakPoints.xs]: 3, [BreakPoints.sm]: 3, [BreakPoints.md]: 4, [BreakPoints.lg]: 3, [BreakPoints.xl]: 3, [BreakPoints.xxl]: 4, [BreakPoints.xxxl]: 4 };
    const breakpoint = Utilities.getCurrentBootstrapBreakpoint(this.window);

    this.myFrequentSites$ = this.myCollabActivityService.getMyFrequentSites(amountToLoad[breakpoint])
      .pipe(
        mergeMap(resources => {
          const mapOfSites = new Map<string, Change[]>();
          const siteChangeRequests = resources.map(resource => {
            const mapKey = `${resource.resourceReference.webUrl},${resource.resourceVisualization.title}`;
            mapOfSites.set(mapKey, []);
            return this.myCollabActivityService
              .getChangesForSite(resource)
              .pipe(map((changes) => {
                changes.forEach(change => {
                  change.UserPhoto$ = change.EditorEmailHint ? this.userService.getUserPhotoByUpn(change.EditorEmailHint) : of(Constants.TRANSPARENT_IMAGE);
                  let arr = [change];
                  if (mapOfSites.has(mapKey)) {
                    arr = mapOfSites.get(mapKey).concat([change]);
                  }
                  mapOfSites.set(mapKey, arr);
                });
                return mapOfSites;
              }));
          });
          return merge(...siteChangeRequests);
        })
      );
  }

  getChangeType(change: ChangeType) {
    return ChangeType[change];
  }

  getPreferredNameLetters(userName: string) {
    return Utilities.generateInitials(userName);
  }
}
@NgModule({
  declarations: [MyCollaborationActivitiesWidgetComponent],
  exports: [MyCollaborationActivitiesWidgetComponent],
  imports: [
    CommonModule,
    ActionLinkModule,
    TextPlaceholderModule,
    WidgetPlaceholderModule,
    SafeModule,
    LinkModule,
  ]
})
export class MyCollaborationActivitiesModule { }
